/**
 * CSV/Excel形式のデータをパースするユーティリティ
 */

export interface ParsedData {
  headers: string[];
  rows: (string | number)[][];
}

/**
 * タブ区切りのテキスト（Excelからコピーした形式）をパース
 */
export function parseTabSeparated(text: string): ParsedData {
  const lines = text.split(/\r?\n/).filter((line) => line.trim() !== '');
  if (lines.length === 0) {
    return { headers: [], rows: [] };
  }

  // 最初の行をヘッダーとして扱う
  const headers = lines[0].split('\t').map((h) => h.trim());
  const rows: (string | number)[][] = [];

  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split('\t');
    const row: (string | number)[] = [];
    
    for (let j = 0; j < headers.length; j++) {
      const value = values[j]?.trim() || '';
      // 数値に変換できる場合は数値として扱う
      const numValue = parseFloat(value);
      row.push(isNaN(numValue) || value === '' ? value : numValue);
    }
    
    rows.push(row);
  }

  return { headers, rows };
}

/**
 * CSV形式のテキストをパース
 */
export function parseCSV(text: string): ParsedData {
  const lines = text.split(/\r?\n/).filter((line) => line.trim() !== '');
  if (lines.length === 0) {
    return { headers: [], rows: [] };
  }

  // CSVのパース（シンプルな実装、引用符は考慮）
  function parseCSVLine(line: string): string[] {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          // エスケープされた引用符
          current += '"';
          i++;
        } else {
          // 引用符の開始/終了
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        // フィールドの区切り
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    result.push(current.trim());
    return result;
  }

  // 最初の行をヘッダーとして扱う
  const headers = parseCSVLine(lines[0]);
  const rows: (string | number)[][] = [];

  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i]);
    const row: (string | number)[] = [];
    
    for (let j = 0; j < headers.length; j++) {
      const value = values[j] || '';
      // 数値に変換できる場合は数値として扱う
      const numValue = parseFloat(value);
      row.push(isNaN(numValue) || value === '' ? value : numValue);
    }
    
    rows.push(row);
  }

  return { headers, rows };
}

/**
 * データをタブ区切りテキスト（Excel貼り付け形式）に変換
 */
export function toTabSeparated(headers: string[], rows: (string | number)[][]): string {
  const lines: string[] = [headers.join('\t')];
  
  for (const row of rows) {
    const values = row.map((v) => {
      if (typeof v === 'number') {
        return v.toString();
      }
      // タブや改行が含まれる場合は引用符で囲む
      if (v.includes('\t') || v.includes('\n') || v.includes('\r')) {
        return `"${v.replace(/"/g, '""')}"`;
      }
      return v;
    });
    lines.push(values.join('\t'));
  }
  
  return lines.join('\n');
}

/**
 * データをCSV形式に変換
 */
export function toCSV(headers: string[], rows: (string | number)[][]): string {
  function escapeCSVValue(value: string | number): string {
    const str = typeof value === 'number' ? value.toString() : value;
    
    // 引用符、カンマ、改行が含まれる場合は引用符で囲む
    if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    
    return str;
  }

  const lines: string[] = [headers.map(escapeCSVValue).join(',')];
  
  for (const row of rows) {
    const values = row.map(escapeCSVValue);
    lines.push(values.join(','));
  }
  
  return lines.join('\n');
}

